import { resolveGameFormat } from '@/lib/gameFormat';
import { validateGameScore } from '@/lib/scoring';
import type { GameFormat } from '@/types/game-format';
import type { Tables } from '@/types/supabase';
import { pointsForGame } from './gameTarget';

type StoredFormat = Partial<
  Pick<Tables<'match'>, 'best_of' | 'points_per_game' | 'deciding_game_points' | 'deuce_rule'>
>;
export function isAlternateGameFormat(format: StoredFormat): boolean {
  return (
    [3, 5].includes(format.best_of ?? 3) &&
    format.deciding_game_points != null &&
    format.deciding_game_points !== (format.points_per_game ?? 11)
  );
}
/** Existing null snapshots never inherit a subsequently configured deciding target. */
export function correctionGameFormat(
  schedule: StoredFormat,
  event: Partial<GameFormat> | null | undefined,
  canonical?: StoredFormat | null,
): ReturnType<typeof resolveGameFormat> {
  const resolved = resolveGameFormat({
    schedule: {
      best_of: schedule.best_of ?? null,
      points_per_game: schedule.points_per_game ?? null,
      deciding_game_points: schedule.deciding_game_points ?? null,
      deuce_rule: schedule.deuce_rule ?? null,
    },
    eventGameFormat: event ?? undefined,
  });
  if (!canonical) return resolved;
  if (canonical.deciding_game_points == null) return { ...resolved, decidingGamePoints: null };
  return {
    bestOf: (canonical.best_of ?? 3) as GameFormat['bestOf'],
    pointsPerGame: canonical.points_per_game ?? 11,
    decidingGamePoints: canonical.deciding_game_points,
    deuceRule: (canonical.deuce_rule ?? 'win_by_2') as ReturnType<
      typeof resolveGameFormat
    >['deuceRule'],
  };
}
export function matchesScoringFormat(
  match: StoredFormat,
  settings: Partial<GameFormat> | null | undefined,
): boolean {
  if (match.deciding_game_points == null) return settings?.decidingGamePoints == null;
  return (
    settings?.decidingGamePoints === match.deciding_game_points &&
    settings.bestOf === match.best_of &&
    settings.pointsPerGame === match.points_per_game &&
    settings.deuceRule === match.deuce_rule
  );
}
export function validateCompletedMatchScores(
  games: unknown,
  format: GameFormat,
  player1Games: number,
  player2Games: number,
): boolean {
  if (!Array.isArray(games) || games.length > format.bestOf) return false;
  const needed = Math.floor(format.bestOf / 2) + 1;
  let a = 0,
    b = 0;
  for (let i = 0; i < games.length; i++) {
    const game = games[i] as { player1Score: number; player2Score: number };
    if (
      a >= needed ||
      b >= needed ||
      !game ||
      !Number.isInteger(game.player1Score) ||
      !Number.isInteger(game.player2Score)
    )
      return false;
    const result = validateGameScore(
      game.player1Score,
      game.player2Score,
      pointsForGame(format, i + 1),
      format.deuceRule,
    );
    if (!result.valid) return false;
    if (result.winner === 'player1') a++;
    else b++;
  }
  return Math.max(a, b) === needed && a === player1Games && b === player2Games;
}
